import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Image as ImageIcon, Package } from 'lucide-react';
import { api } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';
import { Pagination } from '../../components/admin/Pagination';
import { SortableTh, TableSkeleton } from '../../components/admin/SortableTable';
import { TableEmpty } from '../../components/admin/TableEmpty';
import { useDebounce } from '../../hooks/useDebounce';
import { useCurrency } from '../../context/CurrencyContext';
import { TagChecklist } from '../../components/admin/TagChecklist';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const empty = { name: '', description: '', price: 0, images: [], sizes: [], colors: [], collections: [], featured: false, active: true, stock_status: 'in_stock', category: '', meta_title: '', meta_description: '' };
const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Free Size'];
const COLOR_OPTIONS = ['Black', 'White', 'Ivory', 'Beige', 'Camel', 'Navy', 'Burgundy', 'Emerald', 'Rose', 'Gold', 'Silver'];

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [collections, setCollections] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const PAGE_SIZE = 10;
    const debouncedSearch = useDebounce(search);
    const [deleteId, setDeleteId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sort, setSort] = useState({ by: 'created_at', dir: 'desc' });
    const [loading, setLoading] = useState(true);
    const { format } = useCurrency();

    const load = async () => {
        setLoading(true);
        try {
            const [p, c] = await Promise.all([
                api.get('/admin/products', { params: { page, page_size: PAGE_SIZE, search: debouncedSearch || undefined, sort_by: sort.by, sort_dir: sort.dir } }),
                api.get('/collections'),
            ]);
            setItems(p.data.items);
            setTotal(p.data.total);
            setCollections(c.data);
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [page, debouncedSearch, sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [debouncedSearch, sort]);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name, description: p.description, price: p.price,
            images: p.images, sizes: p.sizes, colors: p.colors,
            collections: p.collections, featured: p.featured, active: p.active,
            stock_status: p.stock_status, category: p.category,
            meta_title: p.meta_title || '', meta_description: p.meta_description || '',
        });
        setOpen(true);
    };

    const onImage = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        const b64s = await Promise.all(files.map(fileToBase64));
        setForm((f) => ({ ...f, images: [...f.images, ...b64s] }));
    };

    const addImageUrl = () => {
        const url = window.prompt('Paste image URL');
        if (url) setForm((f) => ({ ...f, images: [...f.images, url] }));
    };

    const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

    const toggleCollection = (slug) => setForm((f) => ({
        ...f,
        collections: f.collections.includes(slug) ? f.collections.filter((s) => s !== slug) : [...f.collections, slug],
    }));

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = { ...form, price: Number(form.price) };
            if (editing) await api.put(`/admin/products/${editing.id}`, payload);
            else await api.post('/admin/products', payload);
            toast.success(editing ? 'Product updated.' : 'Product created.');
            setOpen(false); await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const onDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/admin/products/${deleteId}`);
            toast.success('Product deleted.');
            setDeleteId(null); await load();
        } catch { toast.error('Delete failed'); }
    };

    const filtered = items;

    return (
        <div data-testid="admin-products-page">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                    <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Products</h1>
                </div>
                <button onClick={openNew} data-testid="admin-products-new-button" className="btn-burgundy px-5 py-2.5 rounded-xl text-xs tracking-[0.20em] uppercase inline-flex items-center gap-2 whitespace-nowrap">
                    <Plus className="w-4 h-4" /> New Product
                </button>
            </div>

            <div className="flex items-center justify-between mb-3">
                <input data-testid="admin-products-search-input" type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-luxury max-w-sm" />
                <p className="text-xs whitespace-nowrap ml-4 hidden sm:block" style={{ color: 'var(--ink-muted)' }} data-testid="admin-products-count">{total} product{total === 1 ? '' : 's'}</p>
            </div>

            <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="admin-products-table">
                    <thead className="bg-[var(--brand-rose)]/40">
                        <tr className="text-left">
                            <th className="p-4">Image</th>
                            <SortableTh label="Name" field="name" sort={sort} onSort={setSort} />
                            <SortableTh label="Price" field="price" sort={sort} onSort={setSort} />
                            <th className="p-4">Featured</th>
                            <th className="p-4">Active</th>
                            <th className="p-4">Collections</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    {loading ? <TableSkeleton cols={7} /> : (
                    <tbody>
                        {filtered.map((p) => (
                            <tr key={p.id} className="border-t border-[rgba(90,36,50,0.08)] hover:bg-[var(--brand-rose)]/20 transition-colors" data-testid={`admin-product-row-${p.slug}`}>
                                <td className="p-4">
                                    <div className="w-14 h-16 rounded-md overflow-hidden bg-[var(--brand-cream)]">
                                        {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                                    </div>
                                </td>
                                <td className="p-4 font-serif text-lg" style={{ color: 'var(--brand-burgundy)' }}>{p.name}</td>
                                <td className="p-4">{format(p.price)}</td>
                                <td className="p-4">{p.featured ? 'Yes' : 'No'}</td>
                                <td className="p-4">{p.active ? 'Yes' : 'No'}</td>
                                <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{p.collections.join(', ')}</td>
                                <td className="p-4 text-right">
                                    <button onClick={() => openEdit(p)} data-testid={`admin-product-edit-${p.slug}`} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded" aria-label="Edit"><Pencil className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                    <button onClick={() => setDeleteId(p.id)} data-testid={`admin-product-delete-${p.slug}`} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded" aria-label="Delete"><Trash2 className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    )}
                </table>
                </div>
                {!loading && filtered.length === 0 && (
                    <TableEmpty icon={Package} title="No products yet" subtitle="Create your first product to see it here." testId="admin-products-empty" />
                )}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testIdPrefix="admin-products-pagination" />

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-product-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>{editing ? 'Edit Product' : 'New Product'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="eyebrow block mb-1">Name</label>
                            <input data-testid="admin-product-name" className="input-luxury" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <label className="eyebrow block mb-1">Description</label>
                            <RichTextEditor
                                value={form.description}
                                onChange={(v) => setForm({ ...form, description: v })}
                                placeholder="Describe the piece — fabric, fit, story..."
                                minHeight={180}
                                testId="admin-product-description-editor"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="eyebrow block mb-1">Price (SAR)</label>
                                <input data-testid="admin-product-price" type="number" step="0.01" className="input-luxury" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                            </div>
                            <div>
                                <label className="eyebrow block mb-1">Category</label>
                                <input className="input-luxury" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <TagChecklist label="Sizes" options={SIZE_OPTIONS} value={form.sizes} onChange={(v) => setForm({ ...form, sizes: v })} testIdPrefix="admin-product-sizes" />
                            <TagChecklist label="Colors" options={COLOR_OPTIONS} value={form.colors} onChange={(v) => setForm({ ...form, colors: v })} testIdPrefix="admin-product-colors" />
                        </div>
                        <div>
                            <label className="eyebrow block mb-1">Collections</label>
                            <div className="flex flex-wrap gap-2">
                                {collections.map((c) => (
                                    <button
                                        key={c.slug}
                                        type="button"
                                        onClick={() => toggleCollection(c.slug)}
                                        className="px-3 py-1.5 rounded-lg text-xs border"
                                        style={{
                                            borderColor: form.collections.includes(c.slug) ? 'var(--brand-burgundy)' : 'rgba(90,36,50,0.18)',
                                            backgroundColor: form.collections.includes(c.slug) ? 'var(--brand-burgundy)' : '#fff',
                                            color: form.collections.includes(c.slug) ? '#fff' : 'var(--ink)',
                                        }}
                                    >{c.name}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Images</label>
                            <div className="grid grid-cols-4 gap-2">
                                {form.images.map((img, i) => (
                                    <div key={img || `img-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-[var(--brand-cream)]">
                                        <img src={img} alt={`img-${i}`} className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1"><X className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 flex gap-2">
                                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs cursor-pointer hover:bg-[var(--brand-rose)]/30">
                                    <ImageIcon className="w-4 h-4" /> Upload
                                    <input type="file" accept="image/*" multiple className="hidden" onChange={onImage} data-testid="admin-product-image-upload" />
                                </label>
                                <button type="button" onClick={addImageUrl} className="px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs hover:bg-[var(--brand-rose)]/30">Add by URL</button>
                            </div>
                        </div>
                        <div className="flex items-center gap-6 pt-2">
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
                        </div>

                        <div className="border-t border-[rgba(90,36,50,0.10)] pt-4">
                            <p className="eyebrow mb-3" style={{ color: 'var(--brand-burgundy)' }}>SEO (optional)</p>
                            <div className="space-y-3">
                                <div>
                                    <label className="eyebrow block mb-1">Meta Title</label>
                                    <input className="input-luxury" placeholder={`${form.name || 'Product'} | by anfal`} value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} data-testid="admin-product-meta-title" />
                                </div>
                                <div>
                                    <label className="eyebrow block mb-1">Meta Description</label>
                                    <textarea rows={2} className="input-luxury resize-none" placeholder="Short SEO summary (150-160 characters)" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} data-testid="admin-product-meta-description" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-xs uppercase tracking-[0.18em] hover:bg-[var(--brand-rose)]/40">Cancel</button>
                        <button onClick={onSave} disabled={saving} data-testid="admin-product-save-button" className="btn-burgundy px-5 py-2.5 rounded-xl text-xs uppercase tracking-[0.18em] disabled:opacity-60">{saving ? 'Saving...' : 'Save'}</button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete product?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="btn-burgundy">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
